/**
 * 運営者プロフィールの共通コンポーネント.
 * - AuthorBox: 記事末尾 / About ページ用の小型カード (E-E-A-T)
 * - ProfileSection: ランディングの大きめプロフィールセクション
 *
 * 正本は OPERATOR_PROFILE_HANDOFF.md (2026-06 確定).
 * "AIコンサルタント" 含む最新の肩書きを使用.
 */

import React from 'react';
import { Factory, Cpu, Brain, Wrench, ArrowRight } from 'lucide-react';

const PROFILE = {
  name: '國分 良太',
  jobTitleShort: '制御設計エンジニア → AI・IoT・DX推進｜AIコンサルタント',
  shortBio: '製造業の現場で設備設計・改善プロジェクト・品質向上施策に従事。なぜなぜ分析（RCA）やリスクアセスメントの実務経験をもとに、現場DXを支援するアプリケーションの開発と情報発信に取り組んでいます。AIコンサルタントとして、企業のAI・生成AI活用や現場DX導入の支援も行っています。',
  longBio: '東京の製造業メーカーにて開発部門を担当。電気制御設計を起点に、設備設計・改善プロジェクト・品質向上施策に従事しながら、IoT・AI・生成AIの現場実装を推進しています。あわせて、AIコンサルタントとして企業のAI・生成AI活用や現場DX導入の支援も行っています。',
  /* 表示は80〜120px。元の 1024px PNG は 2.3MB あり、LPの最大の重量物だった。
     構造化データ(AboutView)は PNG のURLを指したままにする。 */
  imageUrl: '/images/author-profile.webp',
  disclaimer: '※ 本サイトは所属企業とは関係のない個人活動です。記載の見解は筆者個人のものです。',
  socials: {
    linkedin: 'https://www.linkedin.com/in/%E8%89%AF%E5%A4%AA-%E5%9C%8B%E5%88%86-72445224a/',
    x: 'https://x.com/ryotako_aigifts',
    note: 'https://note.com/shiny_liger4141',
  },
};

const SocialIcons: React.FC<{ size?: number }> = ({ size = 20 }) => {
  /* クラス名を組み立てない（ビルド時の走査が拾えない）。literal で書く。 */
  const cls = size === 20 ? 'h-5 w-5' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-3">
      <a href={PROFILE.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-slate-400 hover:text-[#0A66C2] transition-colors">
        <svg className={cls} fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      </a>
      <a href={PROFILE.socials.x} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="text-slate-400 hover:text-black transition-colors">
        <svg className={cls} fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </a>
      <a href={PROFILE.socials.note} target="_blank" rel="noopener noreferrer" aria-label="note" className="text-slate-400 hover:text-[#41C9B4] transition-colors">
        <svg className={cls} viewBox="0 0 24 24" fill="currentColor"><path d="M22.904 6.839l-3.743-3.744a3.672 3.672 0 00-5.192 0L2.658 14.406a1.488 1.488 0 00-.436 1.057v4.879a1.488 1.488 0 001.488 1.488h4.879c.395 0 .773-.157 1.052-.436L20.952 10.08a1.488 1.488 0 000-2.104l-1.095-1.094 3.047-3.047a.496.496 0 000-.701v.701l-.001.004z"/></svg>
      </a>
    </div>
  );
};

/**
 * 記事末尾 / 補助的に置く小型の著者カード.
 * 「詳しいプロフィール →」リンクは onClickAbout 経由で /about へ.
 */
interface AuthorBoxProps {
  onClickAbout?: () => void;
}

export const AuthorBox: React.FC<AuthorBoxProps> = ({ onClickAbout }) => {
  return (
    <div className="my-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-blue-50 via-white to-indigo-50 border border-blue-200/60 shadow-sm">
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
        <div className="flex-shrink-0">
          <img src={PROFILE.imageUrl} alt={PROFILE.name} width={80} height={80} loading="lazy" decoding="async" className="rounded-full ring-2 ring-blue-200 ring-offset-2" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="text-sm text-slate-500 mb-1">著者</p>
          <h4 className="text-lg font-bold text-slate-900">{PROFILE.name}</h4>
          <p className="text-sm text-slate-600 mt-1">
            {PROFILE.jobTitleShort}｜東京の製造業メーカー開発部門
          </p>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            {PROFILE.shortBio}
          </p>
          <div className="flex items-center justify-center sm:justify-start gap-4 mt-3">
            {onClickAbout && (
              <button
                onClick={onClickAbout}
                className="text-sm text-blue-600 font-medium hover:underline"
              >
                詳しいプロフィール →
              </button>
            )}
            <SocialIcons size={20} />
          </div>
          <p className="text-xs text-slate-400 mt-3">
            {PROFILE.disclaimer}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Landing の運営者紹介セクション. 写真 + 経歴タグ.
 */
interface ProfileSectionProps {
  onClickAbout?: () => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({ onClickAbout }) => {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
            <div className="md:flex">
              {/* Left: Photo + Name */}
              <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 flex flex-col items-center justify-center text-white">
                <img src={PROFILE.imageUrl} alt={PROFILE.name} width={120} height={120} loading="lazy" decoding="async" className="rounded-full ring-4 ring-white/30 ring-offset-2 ring-offset-blue-600 mb-4" />
                <h3 className="text-xl font-bold">{PROFILE.name}</h3>
                <p className="text-blue-200 text-sm mt-1">制御設計エンジニア</p>
                <p className="text-blue-200 text-sm">AI・IoT・DX推進</p>
                <p className="text-blue-200 text-sm">AIコンサルタント</p>
                <div className="flex gap-3 mt-6">
                  <a href={PROFILE.socials.linkedin} target="_blank" rel="noopener" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="LinkedIn">
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                  <a href={PROFILE.socials.x} target="_blank" rel="noopener" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="X (Twitter)">
                    <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href={PROFILE.socials.note} target="_blank" rel="noopener" className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="note">
                    <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M22.904 6.839l-3.743-3.744a3.672 3.672 0 00-5.192 0L2.658 14.406a1.488 1.488 0 00-.436 1.057v4.879a1.488 1.488 0 001.488 1.488h4.879c.395 0 .773-.157 1.052-.436L20.952 10.08a1.488 1.488 0 000-2.104l-1.095-1.094 3.047-3.047a.496.496 0 000-.701v.701l-.001.004z"/></svg>
                  </a>
                </div>
              </div>
              {/* Right: Bio + Expertise */}
              <div className="md:w-2/3 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">開発者について</h2>
                <p className="text-slate-700 leading-relaxed mb-6">
                  {PROFILE.longBio}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl"><Factory className="h-5 w-5 text-blue-600 flex-shrink-0" /><span className="text-sm font-medium text-slate-800">重工業の制御設計出身</span></div>
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl"><Cpu className="h-5 w-5 text-green-600 flex-shrink-0" /><span className="text-sm font-medium text-slate-800">IoT新規部門を立ち上げ</span></div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl"><Brain className="h-5 w-5 text-purple-600 flex-shrink-0" /><span className="text-sm font-medium text-slate-800">生成AI・RAGを現場実装</span></div>
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl"><Wrench className="h-5 w-5 text-orange-600 flex-shrink-0" /><span className="text-sm font-medium text-slate-800">RCA・リスクアセス実務経験</span></div>
                </div>
                {onClickAbout && (
                  <button
                    onClick={onClickAbout}
                    className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-800 transition-colors"
                  >
                    詳しいプロフィールを見る<ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const PROFILE_DATA = PROFILE;
